import { Cartesian3, Color, HeadingPitchRoll, Matrix4, Quaternion, ScreenSpaceEventType, Transforms, createGuid, } from "cesium";
import { CesiumPointSelectorType, } from "../../cesium-utils/point-selector/point-selector";
import { scaleSegmentToLength } from "../../site/solid-mesh/simple-structure-geometry";
import { DEFAULT_BOX_PARAMS, buildSolidMeshWithBuilder, } from "../../site/solid-mesh/solid-mesh-builder";
import { isAnchorType, } from "../../site/solid-mesh/types";
/**
 * Command for adding a SolidMesh (supports undo/redo)
 */
class AddSolidMeshCommand {
    _viewer;
    _shapeConfig;
    _attributes;
    _createdMesh = null;
    _solidMeshUnderConstructionsRef;
    _onUndo;
    _anchorWgs84;
    _crsConverter;
    /**
     * Creates an AddSolidMeshCommand
     * @param viewer - Cesium Viewer instance
     * @param shapeConfig - Shape configuration for the mesh
     * @param solidMeshUnderConstructions - Reference to array of meshes under construction
     * @param attributes - Optional mesh attributes (anchorType, bottomZ, etc.)
     * @param onUndo - Callback when undoing (e.g. to clean up anchor map)
     * @param anchorWgs84 - Target anchor position; mesh is translated to align after creation
     */
    constructor(viewer, shapeConfig, solidMeshUnderConstructions, crsConverter, attributes, onUndo, anchorWgs84) {
        this._viewer = viewer;
        this._shapeConfig = shapeConfig;
        this._crsConverter = crsConverter;
        this._attributes = attributes;
        this._solidMeshUnderConstructionsRef = solidMeshUnderConstructions;
        this._onUndo = onUndo ?? (() => { });
        this._anchorWgs84 = anchorWgs84;
    }
    /**
     * Executes the command to create a SolidMesh
     * @param viewer - CesiumView instance
     */
    async execute(viewer) {
        this._createdMesh = await buildSolidMeshWithBuilder({
            cesiumViewer: viewer,
            shapeConfig: this._shapeConfig,
            crsConverter: this._crsConverter,
            id: createGuid(),
            attributes: this._attributes,
        });
        if (this._createdMesh) {
            // Align created mesh anchor to the picked anchor position
            if (this._anchorWgs84) {
                try {
                    const [targetLocalAnchor] = await this._crsConverter.toLocal([
                        this._anchorWgs84,
                    ]);
                    await this._createdMesh.translateToLocalAnchor({
                        targetLocalAnchor,
                    });
                }
                catch (e) {
                    console.warn("[AddSolidMeshCommand] anchor alignment failed:", e);
                }
            }
            this._solidMeshUnderConstructionsRef.push(this._createdMesh);
        }
    }
    /**
     * Undoes the command by disposing the created mesh
     * @param _viewer - CesiumView instance (unused)
     */
    async undo(_viewer) {
        if (this._createdMesh) {
            this._onUndo(this._createdMesh.id);
            this._createdMesh.dispose();
            const idx = this._solidMeshUnderConstructionsRef.findIndex((m) => m.id === this._createdMesh?.id);
            if (idx !== -1) {
                this._solidMeshUnderConstructionsRef.splice(idx, 1);
            }
        }
    }
}
/**
 * Handler for SolidMesh construction mode
 */
export class SolidMeshConstructionHandler {
    _callback;
    _solidMeshUnderConstruction = [];
    _viewer;
    _preview;
    _handler;
    _cesiumPointSelector;
    _site;
    _cesiumViewer;
    _commandManager;
    _finishConstructionHandler;
    _rightClickUndoHandler;
    _mouseMoveDebounce = undefined;
    _isUnderConstruction;
    _currentShapeType = "box";
    // Optional factory for computing initial shape from picked positions
    _shapeFactory = undefined;
    // Multi-point mode state
    _maxPointCount = 1;
    _multiPointLocalPositions = [];
    _multiPointWgs84Positions = [];
    /**
     * Creates a SolidMeshConstructionHandler
     * @param args - Handler arguments containing viewer, site, preview, etc.
     * @param cesiumViewer - CesiumView instance
     */
    constructor(args, cesiumViewer) {
        this._site = args.site;
        this._viewer = args.viewer;
        this._preview = args.preview;
        this._handler = args.handler;
        this._cesiumPointSelector =
            args.cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.Default);
        this._cesiumViewer = cesiumViewer;
        this._isUnderConstruction = false;
        this._commandManager = args.commandManger;
    }
    _debounceHandlerForSphereMovement(movement) {
        if (this._mouseMoveDebounce) {
            clearTimeout(this._mouseMoveDebounce);
            this._mouseMoveDebounce = undefined;
        }
        this._mouseMoveDebounce = setTimeout(async () => {
            await this._functionForPreviewSphereMovement(movement);
            this._mouseMoveDebounce = undefined;
        }, 100);
    }
    async _functionForPreviewSphereMovement(movement) {
        const cartesian = await this._cesiumPointSelector.getPointOnMousePos(movement.endPosition);
        if (cartesian) {
            // If we already have picked at least one point in multi-point mode,
            // keep the preview sphere at the first picked point and draw a
            // guideline from that first point to the current mouse position.
            if (this._multiPointLocalPositions.length > 0) {
                // first picked point in WGS84 is stored in _multiPointWgs84Positions[0]
                const p1 = this._multiPointWgs84Positions[0];
                this._preview.setSpherePosition(p1);
                this._preview.setSphereVisibility(true);
                this._preview.setLineVisibility(true);
                this._preview.setLinePosition([p1, cartesian]);
                this._viewer.scene.requestRender();
            }
            else {
                // default behaviour: move preview sphere with cursor
                this._preview.setSpherePosition(cartesian);
                this._viewer.scene.requestRender();
            }
        }
    }
    /**
     * Compute WGS84 center from an anchor (bottom corner) and default dimensions.
     */
    _anchorToCenterWgs84(anchorWgs84, dimensions) {
        const enuMatrix = Transforms.eastNorthUpToFixedFrame(anchorWgs84);
        const objectOffset = new Cartesian3(dimensions.x / 2, // +East
        -dimensions.y / 2, // -North (anchor=maxY, center=centerY)
        dimensions.z / 2);
        return Matrix4.multiplyByPoint(enuMatrix, objectOffset, new Cartesian3());
    }
    /**
     * Creates a temporary solid mesh at the given WGS84 anchor position.
     * When `shapeFromFactory` is supplied the mesh is created with the given
     * center / dimensions / rotation instead of the DEFAULT 10 m cube.
     */
    async _createMeshAtAnchor(anchorWgs84, shapeFromFactory) {
        let shapeConfig;
        if (shapeFromFactory) {
            const localCenter = new Cartesian3(shapeFromFactory.localCenter.x, shapeFromFactory.localCenter.y, shapeFromFactory.localCenter.z);
            const dims = new Cartesian3(shapeFromFactory.dimensions.x, shapeFromFactory.dimensions.y, shapeFromFactory.dimensions.z);
            let quaternion;
            if (shapeFromFactory.hpr) {
                const hpr = new HeadingPitchRoll(shapeFromFactory.hpr.heading, shapeFromFactory.hpr.pitch, shapeFromFactory.hpr.roll);
                quaternion = Quaternion.fromHeadingPitchRoll(hpr);
            }
            shapeConfig = {
                shapeType: shapeFromFactory.shapeType,
                localCenter,
                dimensions: dims,
                ...(quaternion ? { quaternion } : {}),
            };
        }
        else {
            // Default fallback: 10 m cube centered at bottom-nw corner offset
            const dimensions = DEFAULT_BOX_PARAMS.dimensions;
            const wgs84Center = this._anchorToCenterWgs84(anchorWgs84, dimensions);
            shapeConfig = {
                shapeType: this._currentShapeType,
                wgs84Center,
            };
        }
        const meshAttributes = {};
        const suggestedAnchor = this._callback?.meta?.anchorType;
        if (typeof suggestedAnchor === "string" && isAnchorType(suggestedAnchor)) {
            meshAttributes.anchorType = suggestedAnchor;
        }
        if (shapeFromFactory?.attributes) {
            Object.assign(meshAttributes, shapeFromFactory.attributes);
        }
        const addSolidMeshCommand = new AddSolidMeshCommand(this._viewer, shapeConfig, this._solidMeshUnderConstruction, this._site.crsConverter, Object.keys(meshAttributes).length > 0 ? meshAttributes : undefined, undefined, anchorWgs84);
        await this._commandManager.action(addSolidMeshCommand, this._cesiumViewer);
    }
    async addPointOnLeftClick(event) {
        if (!event)
            return;
        const mousePos = event.position;
        const anchorWgs84 = await this._cesiumPointSelector.getPointOnMousePos(mousePos);
        if (!anchorWgs84) {
            console.error("Failed to get mouse position");
            return;
        }
        if (this._maxPointCount <= 1) {
            // ── Single-point mode ──────────────────────────────────────────────
            let shapeFromFactory;
            if (this._shapeFactory) {
                const [local] = await this._site.convertPointsToLocalSpace([
                    anchorWgs84,
                ]);
                shapeFromFactory = this._shapeFactory([{ x: local.x, y: local.y, z: local.z }], { shapeType: this._currentShapeType });
            }
            await this._createMeshAtAnchor(anchorWgs84, shapeFromFactory);
            this.finish();
        }
        else {
            // ── Multi-point mode ────────────────────────────────────────────────
            const [localPt] = await this._site.convertPointsToLocalSpace([
                anchorWgs84,
            ]);
            const localPos = { x: localPt.x, y: localPt.y, z: localPt.z };
            this._multiPointWgs84Positions.push(anchorWgs84);
            this._multiPointLocalPositions.push(localPos);
            // create a persistent sphere at the picked source (first point)
            try {
                this._preview.addMultiPointMarker(anchorWgs84, Color.RED.withAlpha(0.6));
                // ensure guideline entity is visible immediately (zero-length until mouse moves)
                this._preview.setLineVisibility(true);
                this._preview.setLinePosition([anchorWgs84, anchorWgs84]);
            }
            catch (e) {
                console.warn("Failed to create source sphere for multi-point picker:", e);
            }
            const currentStep = this._multiPointLocalPositions.length - 1;
            this._callback?.onProgress?.({
                step: currentStep,
                localPosition: localPos,
            });
            if (this._multiPointLocalPositions.length >= this._maxPointCount) {
                // All points picked — create temp mesh at P1 then finish
                const p1Wgs84 = this._multiPointWgs84Positions[0];
                let shapeFromFactory = this._shapeFactory
                    ? this._shapeFactory([...this._multiPointLocalPositions], {
                        shapeType: this._currentShapeType,
                    })
                    : undefined;
                if (shapeFromFactory && this._shapeFactory) {
                    const [p1, p2] = this._multiPointLocalPositions;
                    if (!p1 || !p2) {
                        throw new Error("Two-point construction requires both local endpoints");
                    }
                    const isPipe = shapeFromFactory.shapeType === "cylinder";
                    const length = isPipe
                        ? Number(shapeFromFactory.dimensions.z.toFixed(2))
                        : Number(shapeFromFactory.dimensions.x.toFixed(2));
                    const calculatedP2 = isPipe
                        ? scaleSegmentToLength(p1, p2, length, shapeFromFactory.shapeType)
                        : scaleSegmentToLength({ ...p1, z: p1.z }, { ...p2, z: p1.z }, length, shapeFromFactory.shapeType);
                    this._multiPointLocalPositions[1] = calculatedP2;
                    shapeFromFactory = this._shapeFactory([...this._multiPointLocalPositions], { shapeType: shapeFromFactory.shapeType });
                }
                await this._createMeshAtAnchor(p1Wgs84, shapeFromFactory);
                this._finishMultiPoint();
            }
        }
    }
    /**
     * Right-click Undo handler for multi-point mode.
     * Removes the last picked point and reports the undo step via onProgress.
     */
    _handleRightClickUndo(_event) {
        if (this._maxPointCount <= 1)
            return;
        if (this._multiPointLocalPositions.length > 0) {
            this._multiPointLocalPositions.pop();
            this._multiPointWgs84Positions.pop();
            const newStep = this._multiPointLocalPositions.length - 1;
            this._callback?.onProgress?.({ step: newStep, isUndo: true });
            // Remove the sphere entity corresponding to the undone point
            this._preview.removeLastMultiPointMarker();
            // If we've undone back to zero points, hide guideline
            if (this._multiPointLocalPositions.length === 0) {
                this._preview.setLineVisibility(false);
                this._preview.setLinePosition([]);
                try {
                    this._viewer.scene.requestRender();
                }
                catch (_) { }
            }
        }
    }
    _resetMultiPointState() {
        this._maxPointCount = 1;
        this._multiPointLocalPositions = [];
        this._multiPointWgs84Positions = [];
        this._shapeFactory = undefined;
    }
    onKeyPressed(event) {
        switch (event.key) {
            case "Esc":
            case "Escape":
                this.cancel();
                break;
            case "Enter":
                // In multi-point mode, ignore Enter until enough points are collected
                if (this._maxPointCount > 1 &&
                    this._multiPointLocalPositions.length < this._maxPointCount) {
                    break;
                }
                this.finish();
                break;
            default:
        }
    }
    /**
     * Starts construction mode
     * @param callback - Callback for construction events
     * @param shapeType - Shape type to construct
     * @param shapeFactory - Optional factory to compute initial shape from picked position(s)
     */
    start(callback, shapeType, shapeFactory) {
        this._callback = callback;
        this._currentShapeType = shapeType ?? this._currentShapeType;
        this._shapeFactory = shapeFactory;
        this._isUnderConstruction = true;
        this._preview.setSphereVisibility(true);
        this._handler.setInputAction(this._debounceHandlerForSphereMovement.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.setInputAction(this.addPointOnLeftClick.bind(this), ScreenSpaceEventType.LEFT_CLICK);
        this._finishConstructionHandler = this.onKeyPressed.bind(this);
        document.addEventListener("keydown", this._finishConstructionHandler);
        this._callback?.onStart?.();
    }
    /**
     * Starts multi-point construction mode.
     * After each click onProgress is called with { step, localPosition }.
     * After the last click, a temp mesh is created at the first point and
     * onFinish is called with (solidMeshIds, { positions }).
     * Right-click undoes the last picked point.
     *
     * @param callback - Callback for construction events
     * @param shapeType - Shape type to construct ("box" | "cylinder" | "sphere")
     * @param maxPoints - Number of points to collect (e.g. 2 for pipe / retaining wall)
     */
    startMultiPoint(callback, shapeType, maxPoints, shapeFactory) {
        this._maxPointCount = maxPoints;
        this._multiPointLocalPositions = [];
        this._multiPointWgs84Positions = [];
        this.start(callback, shapeType, shapeFactory);
        // NOTE: previously we registered a RIGHT_CLICK input action here to
        // immediately undo the last picked point on raw right-click. That
        // caused the pick to be reset as soon as the user opened the context
        // menu. To avoid surprising automatic undos, we no longer register
        // a RIGHT_CLICK handler. Undo must be invoked explicitly (e.g. via
        // the context menu), by calling `undoLastPickedPoint()` below.
    }
    /**
     * Public API to undo the last picked multi-point position. This is
     * the same logic that used to run on RIGHT_CLICK; call it from the
     * UI (context menu) when the user selects Undo.
     */
    undoLastPickedPoint() {
        this._handleRightClickUndo();
    }
    /**
     * Return the number of points currently picked in multi-point mode.
     * Useful for UI to decide whether Undo should remove a picked point
     * or whether a Cancel action is more appropriate.
     */
    getPickedPointCount() {
        return this._multiPointLocalPositions.length;
    }
    _removeInputActions() {
        if (this._mouseMoveDebounce) {
            clearTimeout(this._mouseMoveDebounce);
            this._mouseMoveDebounce = undefined;
        }
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this._handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        document.removeEventListener("keydown", this._finishConstructionHandler);
    }
    _clearAllRendered() {
        this._preview.setSphereVisibility(false);
        this._preview.setLineVisibility(false);
        this._preview.clearMultiPointMarkers();
        this._viewer.scene.requestRender();
    }
    /**
     * Finishes multi-point construction: adds created mesh to site,
     * calls onFinish with (solidMeshIds, { positions }).
     */
    _finishMultiPoint() {
        const positions = [...this._multiPointLocalPositions];
        this._isUnderConstruction = false;
        this._removeInputActions();
        this._clearAllRendered();
        this._resetMultiPointState();
        if (this._solidMeshUnderConstruction.length > 0) {
            const solidMeshIds = [];
            for (const solidMesh of this._solidMeshUnderConstruction) {
                this._site.addSolidMesh(solidMesh);
                solidMeshIds.push(solidMesh.id);
            }
            this._callback?.onFinish?.(solidMeshIds, { positions });
            this._solidMeshUnderConstruction = [];
        }
        else {
            this._callback?.onFinish?.(undefined, { positions });
        }
        this._commandManager.clear();
    }
    /**
     * Finishes construction mode and adds created meshes to site
     */
    finish() {
        this._isUnderConstruction = false;
        this._removeInputActions();
        this._clearAllRendered();
        if (this._solidMeshUnderConstruction.length > 0) {
            const solidMeshIds = [];
            for (const solidMesh of this._solidMeshUnderConstruction) {
                this._site.addSolidMesh(solidMesh);
                solidMeshIds.push(solidMesh.id);
            }
            this._callback?.onFinish?.(solidMeshIds);
            this._solidMeshUnderConstruction = [];
        }
        else {
            this._callback?.onFinish?.(undefined);
        }
        this._resetMultiPointState();
        this._commandManager.clear();
    }
    /**
     * Cancels construction mode and disposes created meshes
     */
    cancel() {
        const wasUnderConstruction = this._isUnderConstruction;
        this._isUnderConstruction = false;
        this._removeInputActions();
        this._clearAllRendered();
        this._resetMultiPointState();
        if (this._solidMeshUnderConstruction.length > 0) {
            for (const solidMesh of this._solidMeshUnderConstruction) {
                solidMesh.dispose();
            }
            this._solidMeshUnderConstruction = [];
        }
        // Only fire onCancel when construction was genuinely active.
        // If called as a cleanup guard after finish(), _isUnderConstruction is
        // already false and firing onCancel would delete the temp mesh prematurely.
        if (wasUnderConstruction) {
            this._callback?.onCancel?.();
        }
    }
    /**
     * Checks if construction is in progress
     * @returns True if under construction
     */
    isUnderConstruction() {
        return this._isUnderConstruction;
    }
    /**
     * Updates the shape type during construction
     * @param shapeType - New shape type
     */
    setShapeType(shapeType) {
        this._currentShapeType = shapeType;
    }
}
//# sourceMappingURL=solid-mesh-construction-handler.js.map